/**
 * `Playlist` — one saved subscription's ledger. `docs/design-direction.md` §8.5,
 * `spec.md` §6.6, `docs/plan.md` T44.
 *
 * §8.5 gives MY CRATE its shelf of disc-over-sleeve items; a playlist is not a shelf
 * — it is an ordered list someone built on purpose, so this screen "reuses the ledger
 * layout with the playlist header" (T44's own words) rather than the shelf grid.
 * `crateshelf.module.css` carries both: the shelf rules `MyCrate.tsx` already spends,
 * and — beneath the "Playlist" banner in that same file — the header, rename form,
 * `PLAY ALL` block and ledger-row tools this screen draws from. Nothing here repeats
 * a class the module does not already own.
 *
 * ## The masthead prints only real numbers
 *
 * `header.trackCount` and `header.durationS` come straight off `PlaylistHeaderSchema`
 * — a real `COUNT(*)` and a real sum the server keeps current on every mutation — so,
 * unlike MY CRATE's paged runtime, the meta line can print on the very first frame.
 * Nothing here is summed from a page that might not be the whole list (§12.6).
 *
 * ## `PLAY ALL` needs the *whole* list, so the screen fetches the whole list
 *
 * §6.6 caps one `POST .../tracks` call at 200 ids, but says nothing that caps a
 * playlist's total length, and `GET /api/playlists/:id` still pages its `tracks` at
 * up to 200 per call. "Play all" that only plays the first page is not "play all" —
 * it is "play the first page and call it all" — so {@link Playlist} walks every
 * cursor before it ever renders a track, and only then is `PLAY ALL`'s `PUT
 * /api/queue` guaranteed to carry the complete, correctly ordered id list T44's
 * acceptance criterion 4 asks for.
 *
 * ## Every mutation here is optimistic, and every one has a real rollback
 *
 * Rename, the share toggle, remove and reorder all follow the one shape: apply the
 * new state locally, fire the request, and on failure restore the exact snapshot
 * taken before the tap — never a partial undo, exactly `store/queue`'s own contract
 * for its optimistic mutations. Reorder in particular borrows that store's
 * `beforeId`-of-the-neighbour trick rather than inventing a second one: after the
 * local splice, the id now sitting one slot after the moved track is the
 * `beforeTrackId` the midpoint endpoint wants, and `null` means "the end" exactly as
 * it does for the queue.
 *
 * ## What the share toggle honestly shows
 *
 * `PATCH .../:id` with `isPublic: true` mints a `shareSlug` if the playlist doesn't
 * have one yet; setting it back to `false` **clears** the slug server-side, "because
 * an unshared link must die" (`schemas/playlist.ts`). This screen mirrors exactly
 * that: the slug prints only while `isPublic` is `true`, and it is always the
 * server's own value — never a URL this file assembles, because no verified source
 * names the scheme a public playlist link should take.
 */

import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import type { PlaylistHeader, TrackSummary } from '@bl/shared';

import type { ApiClient, ApiFailure } from '../lib/api';
import { api, displayCode, toApiFailure, USER_MESSAGE_RU } from '../lib/api';
import type { ScreenProps } from '../app/router';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import { Empty } from '../app/states/Empty';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import { TrackRow } from '../components/TrackRow';
import { cx } from '../components/primitives/Rule';
import { Halftone } from '../components/primitives/Halftone';
import { usePlayerStore } from '../store/player';
import { useQueueStore } from '../store/queue';

import { formatRuntime } from './MyCrate';
import styles from './crateshelf.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

export const RENAME_LABEL_RU = 'Название подборки';
export const RENAME_SUBMIT_RU = 'Сохранить';
export const SHARE_TOGGLE_RU = 'Открыть доступ по ссылке';
export const PLAY_ALL_LABEL = 'PLAY ALL';
export const PLAY_ALL_RU = 'Играть всё';
export const TRACKS_LABEL_RU = 'Треки подборки';

/** The row tools: English print, Russian accessible names — `MyCrate`'s convention. */
export const PLAYLIST_ROW_ACTIONS = Object.freeze({
  up: Object.freeze({ print: 'MOVE UP', ru: 'Сдвинуть выше' }),
  down: Object.freeze({ print: 'MOVE DOWN', ru: 'Сдвинуть ниже' }),
  remove: Object.freeze({ print: 'REMOVE', ru: 'Убрать из подборки' }),
});

/** `GET /api/playlists/:id`'s `tracks` query caps at 200 (`PlaylistTracksQuerySchema`). */
export const PLAYLIST_TRACKS_LIMIT = 200;

/** `id` from the route is not a number — an unparseable one never reaches the network. */
const INVALID_PLAYLIST_ID: ApiFailure = Object.freeze({
  code: 'NOT_FOUND',
  message: USER_MESSAGE_RU.NOT_FOUND,
  displayCode: displayCode('NOT_FOUND'),
  status: 0,
  retryable: false,
});

/**
 * `128 записей · 01:38:04` — both figures straight from the header, never summed
 * locally (see the file header).
 */
export function playlistMetaLine(header: PlaylistHeader): string {
  return `${String(header.trackCount)} записей · ${formatRuntime(header.durationS)}`;
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export interface PlaylistDeps {
  readonly client?: ApiClient;
  readonly queue?: typeof useQueueStore;
  readonly player?: typeof usePlayerStore;
  readonly wiring?: Pick<Wiring, 'select'>;
}

export type PlaylistProps = ScreenProps & { readonly deps?: PlaylistDeps };

type PlaylistLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export function Playlist({ params, deps = {} }: PlaylistProps): JSX.Element {
  const client = deps.client ?? api;
  const queue = deps.queue ?? useQueueStore;
  const player = deps.player ?? usePlayerStore;

  const rawId = Number(params.id);
  const validId = Number.isInteger(rawId) && rawId > 0;
  const id = validId ? rawId : -1;

  const [header, setHeader] = useState<PlaylistHeader | null>(null);
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [status, setStatus] = useState<PlaylistLoadStatus>('idle');
  const [loadError, setLoadError] = useState<ApiFailure | null>(null);
  const [actionError, setActionError] = useState<ApiFailure | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [openTrackId, setOpenTrackId] = useState<number | null>(null);

  const currentTrackId = player((state) => state.trackId);
  const isPlaying = player((state) => state.isPlaying);

  /* Walk every cursor before settling — see the file header's "PLAY ALL needs the
     whole list" note. A playlist a user actually curates is small enough that this
     costs nothing next to the one request MY CRATE's own page already makes. */
  const load = useCallback(async (): Promise<void> => {
    if (!validId) {
      setStatus('error');
      setLoadError(INVALID_PLAYLIST_ID);
      return;
    }
    setStatus('loading');
    setLoadError(null);
    try {
      let response = await client.playlist(id, { limit: PLAYLIST_TRACKS_LIMIT });
      let items = [...response.tracks.items];
      let cursor = response.tracks.nextCursor;
      while (cursor !== null) {
        response = await client.playlist(id, { limit: PLAYLIST_TRACKS_LIMIT, cursor });
        items = items.concat(response.tracks.items);
        cursor = response.tracks.nextCursor;
      }
      setHeader(response.playlist);
      setTitleDraft(response.playlist.title);
      setTracks(items);
      setStatus('ready');
    } catch (error) {
      setStatus('error');
      setLoadError(toApiFailure(error));
    }
  }, [client, id, validId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* "Play all" / "play this playlist starting here": one `PUT /api/queue` with the
     full ordered id list (§6.7), then one play command — identical in shape to
     `MyCrate`'s own `play`, because pulling a record from either shelf means the same
     thing. */
  const play = useCallback(
    async (index: number): Promise<void> => {
      await queue.getState().replace(tracks, index);
      const w = deps.wiring ?? getWiring();
      await w.select(index);
    },
    [deps.wiring, tracks, queue],
  );

  const submitRename = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (header === null) return;
      const nextTitle = titleDraft.trim();
      if (nextTitle === '' || nextTitle === header.title) return;
      const before = header;
      setHeader({ ...header, title: nextTitle });
      void (async (): Promise<void> => {
        try {
          const updated = await client.patchPlaylist(id, { title: nextTitle });
          setHeader(updated);
          setTitleDraft(updated.title);
        } catch (error) {
          setHeader(before);
          setTitleDraft(before.title);
          setActionError(toApiFailure(error));
        }
      })();
    },
    [client, header, id, titleDraft],
  );

  const toggleShare = useCallback((): void => {
    if (header === null) return;
    const before = header;
    const nextPublic = !header.isPublic;
    /* Mirrors the server rule verbatim: turning sharing off clears the slug locally
       too, so the UI never shows a link the server has already killed. */
    setHeader({
      ...header,
      isPublic: nextPublic,
      shareSlug: nextPublic ? header.shareSlug : null,
    });
    void (async (): Promise<void> => {
      try {
        const updated = await client.patchPlaylist(id, { isPublic: nextPublic });
        setHeader(updated);
      } catch (error) {
        setHeader(before);
        setActionError(toApiFailure(error));
      }
    })();
  }, [client, header, id]);

  const removeTrack = useCallback(
    (trackId: number): void => {
      const before = tracks;
      const beforeHeader = header;
      const nextTracks = tracks.filter((track) => track.id !== trackId);
      if (nextTracks.length === before.length) return;
      setTracks(nextTracks);
      setHeader((current) =>
        current === null
          ? current
          : { ...current, trackCount: Math.max(0, current.trackCount - 1) },
      );
      setOpenTrackId((current) => (current === trackId ? null : current));
      void (async (): Promise<void> => {
        try {
          await client.removePlaylistTrack(id, trackId);
        } catch (error) {
          setTracks(before);
          setHeader(beforeHeader);
          setActionError(toApiFailure(error));
        }
      })();
    },
    [client, header, id, tracks],
  );

  /* The queue's own `beforeId`-of-the-neighbour trick (`store/queue.ts`'s `reorder`):
     after the local splice, whatever id now sits one slot after the moved track is
     the `beforeTrackId` the midpoint endpoint wants; `null` means "the end". */
  const moveTrack = useCallback(
    (trackId: number, direction: -1 | 1): void => {
      const from = tracks.findIndex((track) => track.id === trackId);
      if (from < 0) return;
      const to = from + direction;
      if (to < 0 || to >= tracks.length) return;
      const moved = tracks[from];
      if (moved === undefined) return;
      const before = tracks;
      const reordered = [...tracks];
      reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      setTracks(reordered);
      const beforeTrackId = reordered[to + 1]?.id ?? null;
      void (async (): Promise<void> => {
        try {
          await client.reorderPlaylist(id, { trackId, beforeTrackId });
        } catch (error) {
          setTracks(before);
          setActionError(toApiFailure(error));
        }
      })();
    },
    [client, id, tracks],
  );

  const retry = useCallback((): void => {
    void load();
  }, [load]);

  if (header === null) {
    return (
      <div className={styles.screen} data-screen="playlist">
        {status === 'error' && loadError !== null ? (
          <ErrorState failure={loadError} grid="ledger" onRetry={validId ? retry : undefined} />
        ) : (
          <Loading grid="ledger" rows={4} />
        )}
      </div>
    );
  }

  const masthead = (
    <header className={styles.playlistHeader}>
      <Halftone as="div">
        <h1 className={cx('t-display', 't-28', styles.mastheadTitle)}>{header.title}</h1>
      </Halftone>

      <p
        className={cx('t-mono', 't-13', styles.cyr, styles.mastheadMeta)}
        data-playlist-meta="true"
      >
        {playlistMetaLine(header)}
      </p>

      <form className={styles.renameForm} onSubmit={submitRename}>
        <label>
          <span className={styles.sr}>{RENAME_LABEL_RU}</span>
          <input
            type="text"
            className={styles.renameInput}
            value={titleDraft}
            maxLength={80}
            aria-label={RENAME_LABEL_RU}
            onChange={(event) => {
              setTitleDraft(event.target.value);
            }}
          />
        </label>
        <button type="submit" className={styles.renameSubmit}>
          {RENAME_SUBMIT_RU}
        </button>
      </form>

      <div className={styles.headerActions}>
        {/* The screen's single inverted block (§5.5, §12.13, T44 acceptance 5). */}
        <button
          type="button"
          className={styles.playAll}
          aria-label={PLAY_ALL_RU}
          data-inverted="true"
          disabled={tracks.length === 0}
          onClick={() => {
            void play(0);
          }}
        >
          {PLAY_ALL_LABEL}
        </button>

        <button
          type="button"
          className={styles.toolButton}
          aria-pressed={header.isPublic}
          onClick={toggleShare}
        >
          {SHARE_TOGGLE_RU}
        </button>
      </div>

      {header.isPublic && header.shareSlug !== null ? (
        <p className={cx('t-mono', 't-11', styles.slug)} data-share-slug="true">
          {header.shareSlug}
        </p>
      ) : null}

      {actionError === null ? null : (
        <p
          className={cx('t-mono', 't-13', styles.cyr, styles.actionError)}
          role="alert"
          data-action-error="true"
        >
          {`${actionError.displayCode} · ${actionError.message}`}
        </p>
      )}
    </header>
  );

  let body: JSX.Element;
  if (status === 'error' && loadError !== null && tracks.length === 0) {
    body = <ErrorState failure={loadError} grid="ledger" onRetry={retry} />;
  } else if (status === 'ready' && tracks.length === 0) {
    /* No dedicated "empty playlist" object in §8.7's table — a playlist with nothing
       cut into it is the same fact as a crate with nothing cut into it, so this reuses
       that variant rather than inventing a fifth pattern the contract never asked for. */
    body = <Empty variant="crate" grid="ledger" />;
  } else if (tracks.length === 0) {
    body = <Loading grid="ledger" rows={4} />;
  } else {
    body = (
      <StateFrame grid="ledger" state="content" role="group" ariaLabel={TRACKS_LABEL_RU}>
        <ul className={styles.list}>
          {tracks.map((track, index) => {
            const open = openTrackId === track.id;
            return (
              <li key={track.id} className={styles.row}>
                <TrackRow
                  as="div"
                  title={track.title}
                  performer={track.performer}
                  durationS={track.durationS}
                  messageId={track.id}
                  seed={track.plate.seed}
                  meta={[track.year, track.catalogueNo]}
                  current={currentTrackId === track.id}
                  playing={currentTrackId === track.id && isPlaying}
                  index={index}
                  onPlay={() => {
                    void play(index);
                  }}
                  onMore={() => {
                    setOpenTrackId((current) => (current === track.id ? null : track.id));
                  }}
                />

                {open ? (
                  <div className={styles.tools} role="group" aria-label={track.title}>
                    <button
                      type="button"
                      className={cx('t-mono', 't-11', styles.toolButton)}
                      aria-label={PLAYLIST_ROW_ACTIONS.up.ru}
                      disabled={index === 0}
                      onClick={() => {
                        moveTrack(track.id, -1);
                      }}
                    >
                      {PLAYLIST_ROW_ACTIONS.up.print}
                    </button>
                    <button
                      type="button"
                      className={cx('t-mono', 't-11', styles.toolButton)}
                      aria-label={PLAYLIST_ROW_ACTIONS.down.ru}
                      disabled={index === tracks.length - 1}
                      onClick={() => {
                        moveTrack(track.id, 1);
                      }}
                    >
                      {PLAYLIST_ROW_ACTIONS.down.print}
                    </button>
                    <button
                      type="button"
                      className={cx('t-mono', 't-11', styles.toolButton)}
                      aria-label={PLAYLIST_ROW_ACTIONS.remove.ru}
                      data-danger="true"
                      onClick={() => {
                        removeTrack(track.id);
                      }}
                    >
                      {PLAYLIST_ROW_ACTIONS.remove.print}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </StateFrame>
    );
  }

  return (
    <div className={styles.screen} data-screen="playlist">
      {masthead}
      {body}
    </div>
  );
}
